import { normalizePanelReferenceUrls } from '../lib/comicCharacterRefs'
import { normalizePanelCharacterIds } from '../lib/comicCharacters'
import { type ComicProjectCreative,normalizeComicProjectCreative } from '../lib/comicCreativeBible'
import { normalizePageCandidates } from '../lib/comicPageCandidates'
import { normalizePageLayout } from '../lib/comicPageLayout'
import { ensureEpisodePages, seedMissingPagePanels } from '../lib/comicPages'
import { normalizePanelCandidates } from '../lib/comicPanelCandidates'
import { normalizePanelCharacterLines } from '../lib/comicPanelCharacterLines'
import { migratePanelDialogueFields } from '../lib/comicPanelDialogue'
import { type ComicProjectSettings,normalizeComicProjectSettings } from '../lib/comicProjectSettings'
import type { ComicEpisode, ComicPage, ComicPanel, ComicProject } from '../lib/comicTypes'
import { isValidPublicId, resolveWirePublicId } from '../lib/publicId'
import { apiRequest } from './client'
import type { ClientUiPublic } from './platformConfig'



type ComicPageDto = {
  id: string
  index: number
  title?: string
  summary?: string
  storyGoal?: string
  visualPrompt?: string
  dialogueHint?: string
  layout?: string
  panelIds?: string[]
  pageImageUrl?: string | null
  pageImageStatus?: string
  pageErrorMessage?: string | null
  pageCreationTaskId?: string | null
  pageCandidates?: {
    id?: string
    imageUrl?: string
    creationTaskId?: string | null
    createdAt?: string
    generation?: Record<string, unknown>
  }[]
  selectedPageCandidateId?: string | null
}

type ComicEpisodeDto = {
  id: string
  index: number
  title: string
  summary?: string
  plotAdvance?: string
  plannedPageCount?: number
  panels: ComicPanelDto[]
  pages?: ComicPageDto[]
  createdAt?: string
}

type ComicPanelDto = {

  id: string

  order: number

  script?: string

  dialogue?: string

  narration?: string

  imageUrl?: string | null

  selectedCandidateId?: string | null

  candidates?: {

    id?: string

    imageUrl?: string

    creationTaskId?: string | null

    createdAt?: string

  }[]

  creationTaskId?: string | null

  status?: string

  errorMessage?: string

  referenceImageUrls?: string[] | null

  characterIds?: string[] | null

  characterLines?: {
    characterId?: string
    characterName?: string
    speech?: string
    thought?: string
    dialogue?: string
  }[] | null

}



export type ComicProjectDto = {

  publicId?: string

  id?: string

  public_id?: string

  title: string

  synopsis: string

  coverUrl: string | null

  settings?: Record<string, unknown> | null

  creative?: Record<string, unknown> | null

  episodeCount: number

  createdAt: string

  updatedAt: string

  episodes: ComicEpisodeDto[]

}



function mapPanel(dto: ComicPanelDto): ComicPanel {

  const status = dto.status

  const normalizedStatus =

    status === 'generating' || status === 'done' || status === 'failed' ? status : 'draft'

  const cand = normalizePanelCandidates(dto)

  return migratePanelDialogueFields({

    id: dto.id,

    order: dto.order,

    script: dto.script ?? '',

    dialogue: dto.dialogue ?? '',

    narration: dto.narration ?? '',

    imageUrl: cand.imageUrl,

    selectedCandidateId: cand.selectedCandidateId,

    candidates: cand.candidates,

    creationTaskId: dto.creationTaskId ?? null,

    status: normalizedStatus,

    errorMessage: dto.errorMessage,

    referenceImageUrls: normalizePanelReferenceUrls(dto.referenceImageUrls),

    characterIds: normalizePanelCharacterIds(dto.characterIds),

    characterLines: normalizePanelCharacterLines(dto.characterLines),

  })

}



function normalizePageImageStatus(raw: unknown): ComicPage['pageImageStatus'] {
  if (
    raw === 'generating' ||
    raw === 'done' ||
    raw === 'failed' ||
    raw === 'pending_selection'
  ) {
    return raw
  }
  return 'draft'
}

function mapPage(dto: ComicPageDto): ComicPage {
  const cand = normalizePageCandidates({
    pageCandidates: dto.pageCandidates,
    selectedPageCandidateId: dto.selectedPageCandidateId,
    pageImageUrl: dto.pageImageUrl,
  })
  return {
    id: dto.id,
    index: dto.index,
    title: dto.title?.trim() || `第 ${dto.index} 页`,
    summary: dto.summary?.trim() || undefined,
    storyGoal: dto.storyGoal?.trim() || undefined,
    visualPrompt: dto.visualPrompt?.trim() || undefined,
    dialogueHint: dto.dialogueHint?.trim() || undefined,
    layout: normalizePageLayout(dto.layout),
    panelIds: Array.isArray(dto.panelIds) ? dto.panelIds.filter(Boolean) : [],
    pageCandidates: cand.pageCandidates,
    selectedPageCandidateId: cand.selectedPageCandidateId,
    pageImageUrl: cand.pageImageUrl,
    pageImageStatus: normalizePageImageStatus(dto.pageImageStatus ?? cand.pageImageStatus),
    pageErrorMessage: dto.pageErrorMessage ?? undefined,
    pageCreationTaskId: dto.pageCreationTaskId ?? null,
  }
}

function mapEpisode(
  dto: ComicEpisodeDto,
  projectCreatedAt?: string,
): ComicEpisode {
  const panels = (dto.panels ?? []).map(mapPanel).sort((a, b) => a.order - b.order)
  const pages = (dto.pages ?? []).map(mapPage).sort((a, b) => a.index - b.index)
  const createdAt = dto.createdAt?.trim() || projectCreatedAt || undefined
  return seedMissingPagePanels(
    ensureEpisodePages({
      id: dto.id,
      index: dto.index,
      title: dto.title,
      summary: dto.summary?.trim() || undefined,
      plotAdvance: dto.plotAdvance?.trim() || undefined,
      plannedPageCount:
        typeof dto.plannedPageCount === 'number' && dto.plannedPageCount > 0
          ? Math.round(dto.plannedPageCount)
          : undefined,
      panels,
      pages,
      createdAt,
    }),
  )
}



export function comicProjectPublicIdFromDto(
  dto: Pick<ComicProjectDto, 'publicId' | 'id' | 'public_id'>,
): string {
  return resolveWirePublicId(dto, 'publicId', 'public_id', 'id')
}

export function isValidComicProjectPublicId(value: string | null | undefined): boolean {
  return isValidPublicId(value)
}

export function mapComicProjectDto(dto: ComicProjectDto, clientUi: ClientUiPublic): ComicProject {

  const rootSettings = normalizeComicProjectSettings(dto.settings ?? {}, clientUi)
  const creative = normalizeComicProjectCreative(dto.creative ?? {})
  const episodes = (dto.episodes ?? []).map((ep) => mapEpisode(ep, dto.createdAt))

  return {
    id: comicProjectPublicIdFromDto(dto),
    title: dto.title,
    synopsis: dto.synopsis ?? '',
    coverUrl: dto.coverUrl,
    creative,
    settings: rootSettings,
    episodeCount: Math.max(dto.episodeCount, episodes.length),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    episodes,
  }
}



function serializeSettings(settings: ComicProjectSettings): Record<string, unknown> {

  return { ...settings }

}



function serializeProjectBody(project: ComicProject) {

  return {

    title: project.title,

    synopsis: project.synopsis,

    coverUrl: project.coverUrl ?? null,

    creative: { ...project.creative },
    settings: serializeSettings(project.settings),
    episodes: project.episodes.map((ep) => {
      const normalized = ensureEpisodePages(ep)
      return {
      id: normalized.id,
      index: normalized.index,
      title: normalized.title,
        summary: normalized.summary ?? '',
        plotAdvance: normalized.plotAdvance ?? '',
        plannedPageCount: normalized.plannedPageCount ?? null,
      createdAt: normalized.createdAt ?? null,
      pages: normalized.pages.map((page) => ({
        id: page.id,
        index: page.index,
        title: page.title,
          summary: page.summary ?? '',
          storyGoal: page.storyGoal ?? '',
          visualPrompt: page.visualPrompt ?? '',
          dialogueHint: page.dialogueHint ?? '',
        layout: page.layout,
        panelIds: page.panelIds,
        pageImageUrl: page.pageImageUrl ?? null,
        selectedPageCandidateId: page.selectedPageCandidateId ?? null,
        pageCandidates: page.pageCandidates ?? [],
        pageImageStatus: page.pageImageStatus ?? 'draft',
        pageErrorMessage: page.pageErrorMessage ?? null,
        pageCreationTaskId: page.pageCreationTaskId ?? null,
      })),

      panels: normalized.panels.map((p) => ({

        id: p.id,

        order: p.order,

        script: p.script,

        dialogue: p.dialogue,

        narration: p.narration ?? '',

        imageUrl: p.imageUrl,

        selectedCandidateId: p.selectedCandidateId,

        candidates: p.candidates,

        creationTaskId: p.creationTaskId,

        status: p.status,

        errorMessage: p.errorMessage ?? null,

        referenceImageUrls:
          p.referenceImageUrls && p.referenceImageUrls.length > 0 ? p.referenceImageUrls : null,

        characterIds:
          p.characterIds && p.characterIds.length > 0 ? p.characterIds : null,

        characterLines:
          p.characterLines && p.characterLines.length > 0 ? p.characterLines : null,

      })),
      }
    }),

  }

}



export function listComicProjects(clientUi: ClientUiPublic) {
  return apiRequest<ComicProjectDto[]>('/v1/comic-projects', { method: 'GET' }).then((rows) =>
    rows
      .map((dto) => mapComicProjectDto(dto, clientUi))
      .filter((project) => isValidComicProjectPublicId(project.id)),
  )
}



export function getComicProject(id: string, clientUi: ClientUiPublic) {
  if (!isValidComicProjectPublicId(id)) {
    return Promise.reject(new Error('漫画作品标识无效'))
  }
  return apiRequest<ComicProjectDto>(`/v1/comic-projects/${encodeURIComponent(id)}`, {
    method: 'GET',
  }).then((dto) => mapComicProjectDto(dto, clientUi))
}



export function createComicProject(
  clientUi: ClientUiPublic,
  input: {
    title: string
    synopsis?: string
    settings: ComicProjectSettings
    creative?: ComicProjectCreative
  },
) {
  return apiRequest<ComicProjectDto>('/v1/comic-projects', {
    method: 'POST',
    body: JSON.stringify({
      title: input.title.trim(),
      synopsis: (input.synopsis ?? '').trim(),
      settings: serializeSettings(input.settings),
      creative: input.creative ?? normalizeComicProjectCreative({}),
    }),
  }).then((dto) => mapComicProjectDto(dto, clientUi))
}

export function createComicProjectFromWorkflow(
  clientUi: ClientUiPublic,
  input: {
    workflowPlan: Record<string, unknown>
    workflowRunId?: string | null
  },
) {
  return apiRequest<ComicProjectDto>('/v1/comic-projects/from-workflow', {
    method: 'POST',
    body: JSON.stringify({
      workflowRunId: input.workflowRunId ?? null,
      workflowPlan: input.workflowPlan,
    }),
  }).then((dto) => mapComicProjectDto(dto, clientUi))
}

export type ComicWorkflowPlanApplyMode =
  | 'append_episodes'
  | 'replace_selected_episode'
  | 'fill_empty_pages'

export function applyComicProjectWorkflowPlan(
  projectId: string,
  clientUi: ClientUiPublic,
  input: {
    workflowPlan: Record<string, unknown>
    applyMode: ComicWorkflowPlanApplyMode
    targetEpisodeId?: string | null
    workflowRunId?: string | null
  },
) {
  if (!isValidComicProjectPublicId(projectId)) {
    return Promise.reject(new Error('漫画作品标识无效'))
  }
  return apiRequest<ComicProjectDto>(`/v1/comic-projects/${encodeURIComponent(projectId)}/apply-workflow-plan`, {
    method: 'POST',
    body: JSON.stringify({
      workflowRunId: input.workflowRunId ?? null,
      applyMode: input.applyMode,
      targetEpisodeId: input.targetEpisodeId ?? null,
      workflowPlan: input.workflowPlan,
    }),
  }).then((dto) => mapComicProjectDto(dto, clientUi))
}

export function updateComicProject(project: ComicProject, clientUi: ClientUiPublic) {
  if (!isValidComicProjectPublicId(project.id)) {
    return Promise.reject(new Error('漫画作品标识无效'))
  }
  return apiRequest<ComicProjectDto>(`/v1/comic-projects/${encodeURIComponent(project.id)}`, {
    method: 'PUT',
    body: JSON.stringify(serializeProjectBody(project)),
  }).then((dto) => mapComicProjectDto(dto, clientUi))
}

export function deleteComicProject(id: string) {
  if (!isValidComicProjectPublicId(id)) {
    return Promise.reject(new Error('漫画作品标识无效'))
  }
  return apiRequest<void>(`/v1/comic-projects/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/** 将供应商成图 URL 转存为站内可长期访问地址（与话级生图 job 同一套存储） */
export function persistComicPageImageUrls(
  projectId: string,
  imageUrls: string[],
  displayBaseName?: string,
  context?: { episodeId?: string; pageId?: string },
) {
  return apiRequest<{ imageUrls: string[] }>(
    `/v1/comic-projects/${encodeURIComponent(projectId)}/persist-page-image-urls`,
    {
      method: 'POST',
      body: JSON.stringify({
        imageUrls,
        displayBaseName: (displayBaseName ?? 'comic_page').trim().slice(0, 160),
        episodeId: context?.episodeId ?? '',
        pageId: context?.pageId ?? '',
      }),
    },
  ).then((body) => body.imageUrls)
}


